import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private baseUrl = environment.uri;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Obtiene la lista de usuarios disponibles para impersonar
   */
  getAvailableUsers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}usuarios/List`);
  }

  /**
   * Cambia al usuario seleccionado por email
   */
  impersonateUserByEmail(email: string): Observable<any> {
    const currentUser = this.authService.getUser;
    
    // Limpiar cualquier dato de impersonación previo para evitar conflictos
    if (localStorage.getItem('impersonatedUserData')) {
      console.log('Limpiando datos de impersonación previos...');
      localStorage.removeItem('impersonatedUserData');
    }
    
    // Guardar usuario original si es la primera vez
    if (!localStorage.getItem('originalUser')) {
      localStorage.setItem('originalUser', JSON.stringify(currentUser));
    }

    // Crear headers con Authorization preservado
    const headers = this.authService.createHeaders();
    const body = { email: email };

    return this.http.post<any>(`${this.baseUrl}Accounts/impersonate`, body, { headers })
      .pipe(
        switchMap(response => {
          // El backend devuelve la misma estructura que el login (IApiUserAuthenticated)
          if (response && response.Email) {
            // Usar exactamente el mismo flujo que el login
            this.authService.setUserLocalStorage(response);
            this.authService.currentUser.next(response);
            
            // Después de impersonar, hacer llamados para obtener datos adicionales
            return this.fetchAdditionalUserData(response);
          }
          // Si no hay respuesta válida, devolver un error
          throw new Error('Respuesta de impersonación inválida o usuario no encontrado');
        }),
        catchError(error => {
          console.error('Error en impersonateUserByEmail:', error);
          
          // RESTAURAR USUARIO ORIGINAL cuando la impersonación falla
          this.restoreOriginalUserOnError();
          
          // Re-lanzar el error para que el componente pueda manejarlo
          throw error;
        })
      );
  }

  /**
   * Obtiene datos adicionales del usuario impersonado (descuentos, datos personales, etc.)
   */
  private fetchAdditionalUserData(userResponse: any): Observable<any> {
    // Crear headers con el nuevo token del usuario impersonado
    const headers = this.authService.createHeaders();
    
    // Hacer llamados paralelos para obtener datos adicionales
    // Descuento, mínima compra y mínima cantidad no se piden acá: los carga la tienda
    // al recargar (getParametrosCompra) y los guarda con setParametrosCompra.
    // Precios y fechas de entrega tampoco se precargan.
    const additionalCalls = forkJoin({
      zonasEntrega: this.http.get<any>(`${this.baseUrl}ZonasDeEntrega/Get`, { headers }).pipe(
        catchError(error => {
          console.warn('Error obteniendo zonas de entrega:', error);
          return of([]);
        })
      )
      // Puedes agregar más llamados según necesites
      // datosPersonales: this.http.get<any>(`${this.baseUrl}usuarios/datosPersonales`, { headers }).pipe(
      //   catchError(error => of([]))
      // )
    });

    return additionalCalls.pipe(
      tap(additionalData => {
        // Guardar datos adicionales en localStorage para uso posterior
        const enrichedUserData = {
          ...userResponse,
          additionalData: {
            zonasEntrega: additionalData.zonasEntrega, // Zonas de entrega disponibles
            // datosPersonales: additionalData.datosPersonales
          }
        };
        
        // Actualizar el usuario en AuthService con los datos enriquecidos
        this.authService.setUserLocalStorage(enrichedUserData);
        this.authService.currentUser.next(enrichedUserData);
        
        // También guardarlo en un storage específico para impersonación
        localStorage.setItem('impersonatedUserData', JSON.stringify(enrichedUserData));

        // Emitir evento para que los componentes se actualicen
        this.notifyDataRefresh(additionalData);
      }),
      // Devolver la respuesta original para mantener compatibilidad
      switchMap(() => of(userResponse)),
      catchError(error => {
        console.error('Error crítico al obtener datos adicionales del usuario impersonado:', error);
        
        // En caso de error crítico en la obtención de datos adicionales,
        // restaurar usuario original
        console.log('Restaurando usuario original debido a error en datos adicionales...');
        this.restoreOriginalUserOnError();
        
        // Re-lanzar el error
        throw new Error(`Error al obtener datos del usuario impersonado: ${error.message || error}`);
      })
    );
  }

  /**
   * Notifica a los componentes que deben refrescar sus datos
   */
  private notifyDataRefresh(data: any): void {
    // Emitir evento personalizado para que los componentes se actualicen
    const event = new CustomEvent('impersonationDataRefresh', { 
      detail: {
        zonasEntrega: data.zonasEntrega
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * Vuelve al usuario original
   */
  stopImpersonation(): Observable<any> {
    // Crear headers con Authorization preservado
    const headers = this.authService.createHeaders();
    
    return this.http.post<any>(`${this.baseUrl}Accounts/stop-impersonation`, {}, { headers })
      .pipe(
        tap(response => {
          // Restaurar usuario original del localStorage
          const originalUser = this.getOriginalUser();
          
          if (originalUser) {
            // LIMPIEZA EXHAUSTIVA: Eliminar TODOS los datos de impersonación
            this.clearImpersonationData();
            
            // También limpiar desde AuthService
            this.authService.cleanImpersonationDataFromAuth();
            
            // SIEMPRE restaurar el usuario original guardado, independientemente de la respuesta del backend
            this.authService.setUserLocalStorage(originalUser);
            this.authService.currentUser.next(originalUser);
            
            // Opcional: Si el backend devuelve nuevo token para el usuario original, actualizar solo el token
            if (response && response.JwtToken) {
              originalUser.JwtToken = response.JwtToken;
              this.authService.setUserLocalStorage(originalUser);
              this.authService.currentUser.next(originalUser);
            }
          } else {
            // Si no hay usuario original guardado, limpiar todo y forzar logout
            console.warn('No se encontró usuario original guardado. Limpiando sesión...');
            this.clearImpersonationData();
            this.authService.cleanImpersonationDataFromAuth();
            // Aquí podrías agregar lógica de logout si es necesario
          }
        }),
        catchError(error => {
          console.error('Error al detener impersonalización en el backend:', error);
          
          // Aunque el backend falle, SIEMPRE intentar restaurar el usuario original
          const originalUser = this.getOriginalUser();
          
          if (originalUser) {
            console.log('Restaurando usuario original localmente a pesar del error del backend...');
            
            // LIMPIEZA EXHAUSTIVA: Eliminar TODOS los datos de impersonación
            this.clearImpersonationData();
            
            // También limpiar desde AuthService
            this.authService.cleanImpersonationDataFromAuth();
            
            // Restaurar el usuario original guardado
            this.authService.setUserLocalStorage(originalUser);
            this.authService.currentUser.next(originalUser);
            
            // Devolver el usuario original en lugar del error para que la aplicación continúe funcionando
            return of(originalUser);
          } else {
            // Si no hay usuario original, limpiar todo por seguridad
            console.warn('No se pudo encontrar el usuario original. Limpiando toda la sesión...');
            this.clearImpersonationData();
            this.authService.cleanImpersonationDataFromAuth();
            
            // Re-lanzar el error ya que no podemos restaurar
            throw new Error(`Error al detener impersonalización y no se pudo restaurar usuario original: ${error.message || error}`);
          }
        })
      );
  }

  /**
   * Verifica si el usuario actual puede hacer impersonación
   */
  canImpersonate(): boolean {
    const currentUser = this.authService.getUser;
    return currentUser ? currentUser.Role !== 'User' : false;
  }

  /**
   * Verifica si actualmente se está impersonando
   */
  isImpersonating(): boolean {
    return !!localStorage.getItem('originalUser');
  }

  /**
   * MÉTODO DE EMERGENCIA: Fuerza la restauración del usuario original
   * Útil para casos donde la impersonalización se rompe o queda en estado inconsistente
   */
  forceRestoreOriginalUser(): boolean {
    console.log('Forzando restauración del usuario original...');
    
    const originalUser = this.getOriginalUser();
    
    if (originalUser) {
      try {
        // Limpiar TODOS los datos de impersonación
        this.clearImpersonationData();
        this.authService.cleanImpersonationDataFromAuth();
        
        // Restaurar usuario original
        this.authService.setUserLocalStorage(originalUser);
        this.authService.currentUser.next(originalUser);
        
        console.log('Usuario original restaurado exitosamente por método de emergencia');
        return true;
      } catch (error) {
        console.error('Error al forzar restauración del usuario original:', error);
        return false;
      }
    } else {
      console.warn('No se pudo encontrar el usuario original para la restauración forzada');
      
      // Limpiar todo por seguridad
      this.clearImpersonationData();
      this.authService.cleanImpersonationDataFromAuth();
      
      return false;
    }
  }

  /**
   * Obtiene información del usuario original (si existe)
   */
  getOriginalUser(): any {
    const originalUserStr = localStorage.getItem('originalUser');
    return originalUserStr ? JSON.parse(originalUserStr) : null;
  }

  /**
   * Obtiene el usuario que está siendo impersonado actualmente
   */
  getCurrentImpersonatedUser(): any {
    return this.isImpersonating() ? this.authService.getUser : null;
  }

  /**
   * Obtiene información completa del usuario impersonado para mostrar en el banner
   */
  getImpersonatedUserDisplayInfo(): any {
    if (!this.isImpersonating()) return null;

    const currentUser = this.authService.getUser;
    if (!currentUser) return null;

    // Obtener datos adicionales si están disponibles
    const additionalData = this.getImpersonatedUserAdditionalData();

    return {
      // Información básica del usuario
      name: `${currentUser.FirstName || ''} ${currentUser.LastName || ''}`.trim(),
      email: currentUser.Email || '',
      role: currentUser.Role || '',
      
      // Información del cliente/empresa (pjf)
      clienteInfo: {
        nombre: currentUser.pjf?.Razonsocial || currentUser.pjf?.NombreFantasia || 'Cliente no especificado',
        telefono: currentUser.pjf?.Telefono || '',
        email: currentUser.pjf?.Email || currentUser.Email,
        domicilio: currentUser.pjf?.Domicilio || '',
        domicilioEntrega: currentUser.pjf?.DomicilioEntrega || currentUser.pjf?.Domicilio || '',
        ciudad: currentUser.pjf?.IdCiudad ? `Ciudad ID: ${currentUser.pjf.IdCiudad}` : '',
        cp: currentUser.pjf?.Cp || currentUser.pjf?.CpEntrega || '',
        cuit: currentUser.pjf?.Nrodoc || '',
        condicionIva: currentUser.pjf?.Condicioniva || ''
      },
      
      // Información de precios y descuentos (ahora con datos del servidor)
      preciosInfo: {
        idListaPrecio: currentUser.idListaPrecio || 0,
        listaExcluidos: currentUser.ListaExcluidos || false,
        nombreLista: `Lista ${currentUser.idListaPrecio || 'Estándar'}`,
        descuentos: additionalData?.descuentos || [],
        minimaCompra: additionalData?.minimaCompra || null,
        minimaCantidad: additionalData?.minimaCantidad || null,
        preciosActualizados: additionalData?.precios ? additionalData.precios.length : 0,
        fechasDisponibles: additionalData?.fechasEntrega ? additionalData.fechasEntrega.length : 0,
        zonasDisponibles: additionalData?.zonasEntrega ? additionalData.zonasEntrega.length : 0
      },
      
      // ID del PJF para referencias
      idPjf: currentUser.IdPjf || 0
    };
  }

  /**
   * Obtiene datos adicionales del usuario impersonado (descuentos, etc.)
   */
  getImpersonatedUserAdditionalData(): any {
    const impersonatedDataStr = localStorage.getItem('impersonatedUserData');
    if (impersonatedDataStr) {
      const userData = JSON.parse(impersonatedDataStr);
      return userData.additionalData || null;
    }
    return null;
  }

  /**
   * Guarda los parámetros de compra (de getParametrosCompra) en los datos de impersonación
   * para que el banner los muestre sin volver a pedirlos.
   */
  setParametrosCompra(parametros: { minimaCompra: number; descuento: number; minimaCantidad: number }): void {
    if (!this.isImpersonating()) return;

    const impersonatedDataStr = localStorage.getItem('impersonatedUserData');
    if (!impersonatedDataStr) return;

    const userData = JSON.parse(impersonatedDataStr);
    userData.additionalData = {
      ...userData.additionalData,
      descuentos: parametros.descuento ? [{ porcentaje: parametros.descuento }] : [],
      minimaCompra: parametros.minimaCompra,
      minimaCantidad: parametros.minimaCantidad
    };
    localStorage.setItem('impersonatedUserData', JSON.stringify(userData));
  }

  /**
   * Obtiene los descuentos específicos del usuario impersonado
   */
  getImpersonatedUserDiscounts(): any[] {
    const additionalData = this.getImpersonatedUserAdditionalData();
    return additionalData?.descuentos || [];
  }

  /**
   * Obtiene la mínima compra del usuario impersonado
   */
  getImpersonatedUserMinimaCompra(): any {
    const additionalData = this.getImpersonatedUserAdditionalData();
    return additionalData?.minimaCompra || null;
  }

  /**
   * Obtiene la mínima cantidad del usuario impersonado
   */
  getImpersonatedUserMinimaCantidad(): any {
    const additionalData = this.getImpersonatedUserAdditionalData();
    return additionalData?.minimaCantidad || null;
  }

  /**
   * Obtiene los precios actualizados del usuario impersonado
   */
  getImpersonatedUserPrecios(): any[] {
    const additionalData = this.getImpersonatedUserAdditionalData();
    return additionalData?.precios || [];
  }

  /**
   * Obtiene las fechas de entrega disponibles para el usuario impersonado
   */
  getImpersonatedUserFechasEntrega(): any[] {
    const additionalData = this.getImpersonatedUserAdditionalData();
    return additionalData?.fechasEntrega || [];
  }

  /**
   * Obtiene las zonas de entrega disponibles para el usuario impersonado
   */
  getImpersonatedUserZonasEntrega(): any[] {
    const additionalData = this.getImpersonatedUserAdditionalData();
    return additionalData?.zonasEntrega || [];
  }

  /**
   * Verifica si hay datos actualizados disponibles
   */
  hasRefreshedData(): boolean {
    const additionalData = this.getImpersonatedUserAdditionalData();
    return !!(additionalData?.precios || additionalData?.fechasEntrega);
  }

  /**
   * Obtiene la dirección principal del usuario impersonado
   */
  getImpersonatedUserMainAddress(): string {
    const displayInfo = this.getImpersonatedUserDisplayInfo();
    if (!displayInfo?.clienteInfo) return '';

    const cliente = displayInfo.clienteInfo;
    // Priorizar dirección de entrega si existe, sino usar domicilio normal
    const direccion = cliente.domicilioEntrega || cliente.domicilio;
    const cp = cliente.cp;
    
    if (direccion && cp) {
      return `${direccion} - CP: ${cp}`;
    } else if (direccion) {
      return direccion;
    }
    
    return 'Dirección no especificada';
  }

  /**
   * Obtiene información de descuentos/precios del usuario impersonado
   */
  getImpersonatedUserPriceInfo(): string {
    const displayInfo = this.getImpersonatedUserDisplayInfo();
    if (!displayInfo?.preciosInfo) return '';

    const precios = displayInfo.preciosInfo;
    let info = `Lista: ${precios.nombreLista}`;
    
    if (precios.listaExcluidos) {
      info += ' (Excluido de promociones)';
    }

    // Agregar información de descuentos si están disponibles
    const descuentos = precios.descuentos;
    if (descuentos && descuentos.length > 0) {
      const descuentoTexto = descuentos.map((d: any) => `${d.porcentaje || d.percentage || 0}%`).join(', ');
      info += ` | Desc: ${descuentoTexto}`;
    }

    // Agregar información de mínima compra
    if (precios.minimaCompra) {
      info += ` | Mín: $${precios.minimaCompra}`;
    }
    
    return info;
  }

  /**
   * Limpia TODOS los datos de impersonación del localStorage
   */
  private clearImpersonationData(): void {
    console.log('Limpiando todos los datos de impersonación del localStorage...');
    
    // Lista de todas las claves relacionadas con impersonación que deben ser eliminadas
    const impersonationKeys = [
      'originalUser',                    // Usuario original guardado antes de impersonar
      'impersonatedUserData',           // Datos del usuario impersonado con información adicional
      'impersonatedUser',               // Por si se usa otra clave
      'impersonation_state',            // Estado de impersonación
      'impersonation_additional_data',  // Datos adicionales de impersonación
      'tempUserData',                   // Datos temporales que puedan quedar
      'userDisplayInfo'                 // Información de display que pueda quedar
    ];

    // Eliminar cada clave de impersonación
    impersonationKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`Eliminando clave de localStorage: ${key}`);
        localStorage.removeItem(key);
      }
    });

    // También limpiar cualquier clave que empiece con 'impersonat' por seguridad
    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (key.toLowerCase().includes('impersonat') && !impersonationKeys.includes(key)) {
        console.log(`Eliminando clave adicional de impersonación: ${key}`);
        localStorage.removeItem(key);
      }
    });

    console.log('Limpieza de datos de impersonación completada.');
  }

  /**
   * Restaura el usuario original cuando falla la impersonalización
   */
  private restoreOriginalUserOnError(): void {
    console.log('Restaurando usuario original debido a error en impersonalización...');
    
    const originalUser = this.getOriginalUser();
    
    if (originalUser) {
      // Restaurar el usuario original
      this.authService.setUserLocalStorage(originalUser);
      this.authService.currentUser.next(originalUser);
      
      // Limpiar datos de impersonación
      this.clearImpersonationData();
      this.authService.cleanImpersonationDataFromAuth();
      
      console.log('Usuario original restaurado exitosamente:', originalUser.Email);
    } else {
      console.warn('No se pudo encontrar el usuario original para restaurar.');
      
      // Si no hay usuario original, limpiar todo por seguridad
      this.clearImpersonationData();
      this.authService.cleanImpersonationDataFromAuth();
    }
  }
}